import * as React from "react";
import { FC, useEffect, useRef } from "react";
import { Feature, Map } from "ol";
import { useMap } from "./useMap";
import { SelectedStatus } from "./enums";
import { GeoLocation } from "./GeoLocation";
import { FilterOptions } from "./filters";
import { Popup } from "./Popup";
import Portal from "./Portal";
import "../css/icons/style.css";
import "../css/map.css";
import "ol/ol.css";

export type OnFeatureClickCallback = (feature: Feature) => void;
export type OnUnselectCallback = (feature: Feature) => void;

export interface IMammadabaMapProps {
  /**
   * (Required) All of the features, with custom data fields added, such as "VISITED", "LIKE" etc
   */
  allFeatures: Feature[] | null;
  /**
   * (optional) array of filtered objects (features), visible on map
   * @default [] means, no objects (features) visible on map, only basemap is visible
   */
  features: Feature[] | null;
  /**
   * callback on object click, returns identified object (feature)
   */
  onClickOnFeature: OnFeatureClickCallback;
  /**
   * callback, when object unselected, returns OBJECTID of unselected feature
   */
  onUnselect: OnUnselectCallback;
  /**
   * boolean flag, sets if map visible/hidden
   */
  mapVisible: boolean;
  /**
   * feature, to zoom-to
   */
  zoomTo: Feature | null;
  /**
   * boolean flag, show or hide circle with nearby filter radius
   */
  nearbyCircleVisible: boolean;
  /**
   * map padding, left, in px, animation duration, in ms
   * NOTE: for map transitions, easing function is "ease-in-out", so it is reccomended to use it for sidebar too
   * @example { left: 300, duration: 500 }
   */
  padding: IPadding;
  /**
   * filter options, to use for filters
   */
  filterOptions: FilterOptions;
  /**
   * show popup, at click coordinates, content as html pass here
   */
  popupHtml: HTMLDivElement | TrustedHTML | null;
  /**
   * map starting position
   */
  startPosition: { x: number; y: number; zoom: number };
}

export interface IVisibleLayer {
  layerId: string;
  visibility: boolean;
}

export interface IUpdates {
  selectedState?: SelectedStatus;
  visited?: number;
  like?: number;
}

export interface IPadding {
  left: number;
  duration: number;
}

export interface ILayerFeatures {
  layerId: string;
  features: Feature[];
}

let mapRef: React.MutableRefObject<Map>;

export const MammadabaMap: FC<IMammadabaMapProps> = (props) => {
  const ref = useRef<HTMLDivElement>();

  mapRef = useRef<Map>();
  mapRef.current = useMap(ref, props);
  // @ts-ignore
  window.allFeatures = props.allFeatures;

  const [locationButton, setLocationButton] = React.useState<boolean>(false);

  const style = props.mapVisible
    ? { height: "100%", display: "block" }
    : { display: "none" };

  useEffect(
    () => document.getElementById("location-button") && setLocationButton(true),
    []
  );

  return (
    <>
      <div ref={ref} style={style} />
      {locationButton ? (
        <Portal id={"location-button"}>
          <GeoLocation map={mapRef.current} />
        </Portal>
      ) : null}

      {mapRef.current && <Popup content={props.popupHtml} />}
    </>
  );
};

export const updateAttributes = (
  feature: Feature,
  updates: IUpdates,
  allFeatures: Feature[]
): void => {
  if (updates) {
    updates.visited !== undefined && feature.set("VISITED", updates.visited);
    updates.like !== undefined && feature.set("LIKE", updates.like);

    if (
      updates.selectedState === SelectedStatus.Default ||
      updates.selectedState === SelectedStatus.Selected
    ) {
      for (const feature of allFeatures) {
        if (feature.get("SELECTED") !== SelectedStatus.Default) {
          feature.set("SELECTED", SelectedStatus.Default);
        }
      }
      feature.set("SELECTED", updates.selectedState);
    }
  }
  feature.changed();
};

export const getMap = (): Map => mapRef.current;
